import get from "lodash/fp/get";
import { createSelector } from "reselect";
import { ModuleTemplate } from "src/requests/modules/types";
import { ModuleId, TemplateId } from "../../globalTypes";
import {
  getAllTemplateMetadataById,
  getAllVisibleOverviewIds,
  getTemplateMetadataFromId,
} from "../templateMetadata/selectors";
import { getIsTopLevel } from "../templateMetadata/thunks/utils";
import { RootState } from "../types";
import { ModulesStoreSlice, SModuleMetadata } from "./types";
import { sortModulesOrTemplates } from "./utils";

// Slice
export const getModulesSlice = (state: RootState): ModulesStoreSlice =>
  state.modules;

export const getModulesMetadata = (state: RootState) =>
  getModulesSlice(state).metadata;

export const getAllModulesMetadataById = (state: RootState) =>
  getModulesMetadata(state).byId;

export const getMetadataForModuleId = (
  state: RootState,
  id: ModuleId
): SModuleMetadata | undefined => getAllModulesMetadataById(state)[id];

// isFetching

export const getIsMetadataFetching = (state: RootState) =>
  getModulesMetadata(state).isFetching;

export const getIsMetadataFetchingForModuleId = (
  state: RootState,
  id: ModuleId
) => getMetadataForModuleId(state, id)?.isFetching;

// Module

export const getNameForModuleId = (state: RootState, id: ModuleId) =>
  get("name", getMetadataForModuleId(state, id)); // "name" nemuze byt pouzite za ?.

export const getIconForModuleId = (state: RootState, id: ModuleId) =>
  getMetadataForModuleId(state, id)?.image;

export const getTemplateIdsForModuleId = (state: RootState, id: ModuleId) =>
  getMetadataForModuleId(state, id)?.templates;

// Modules
export const getIdsOfAllModules = createSelector(
  getAllModulesMetadataById, // Do budoucna se budeme muset nějak postavit k tomu, že budeme uchovávat víc seznamů
  (byId) => Object.keys(byId).map(Number)
);

const passId = (state: RootState, id: TemplateId) => id;

export const getOrderedTemplatesByModuleId = createSelector(
  passId,
  getAllModulesMetadataById,
  (moduleId, metadataById) => {
    const metadata = metadataById[moduleId];
    if (!metadata?.templates) return [];

    return [...metadata.templates].sort(sortModulesOrTemplates);
  }
);

export const getTemplateModuleMetadataById = createSelector(
  passId,
  getAllModulesMetadataById,
  (templateId, metadataById) => {
    const templatesMetadata = Object.values(metadataById)
      .map(({ templates }) => templates ?? ({} as ModuleTemplate))
      .flat();

    return templatesMetadata.find((template) => template?.id === templateId);
  }
);

export const getIdsOfVisibleModulesOrderedByModuleOrder = createSelector(
  getIdsOfAllModules,
  getAllModulesMetadataById,
  (allIds, metadataById) =>
    allIds
      .slice() // udělám kopii abych nemutoval výsledek předchozího selectoru - NUTNÉ
      .filter(
        (id) =>
          metadataById[id] && metadataById[id].visible && metadataById[id].order
      )
      .sort((idA, idB) =>
        sortModulesOrTemplates(metadataById[idA], metadataById[idB])
      )
);

export const makeGetIdsOfAllVisibleOverviewTemplatesForModuleId2 = () =>
  createSelector(
    getTemplateIdsForModuleId,
    getAllVisibleOverviewIds,
    (templateIds, visibleOverviewIds) => {
      if (!templateIds || !visibleOverviewIds) return [];
      return templateIds.filter(({ id }) => visibleOverviewIds.includes(id));
    }
  );

export const makeGetIdsOfAllVisibleOverviewTemplatesForModuleId = () =>
  createSelector(
    passId,
    getAllTemplateMetadataById,
    (moduleId, templatesMetadata) => {
      const filteredTemplates = Object.values(templatesMetadata).filter(
        (templateData) =>
          templateData.moduleId === moduleId &&
          getIsTopLevel(templateData.type) &&
          templateData.visible
      );

      const orderedTemplates = filteredTemplates.sort(sortModulesOrTemplates);

      return orderedTemplates.map(({ id }) => id ?? 0);
    }
  );

export const getModuleIdByTemplateId = createSelector(
  getAllModulesMetadataById,
  passId,
  (allModulesById, id) => {
    let moduleId = null;
    Object.values(allModulesById).forEach((module) => {
      if (module.templates) {
        module.templates.forEach(({ id: templateId }) => {
          if (templateId === Number(id) && module.id) {
            moduleId = module.id;
          }
        });
      }
    });
    return moduleId;
  }
);

export const getModuleNameByTemplateId = createSelector(
  getAllModulesMetadataById,
  getTemplateMetadataFromId,
  (allModulesById, templateMetadata) => {
    const { moduleId } = templateMetadata || {};
    if (!moduleId) return ""; // nemám template metadata

    const moduleMetadata = allModulesById[moduleId];
    if (!moduleMetadata) return ""; // nemám module metadata ve slovníku
    if (!moduleMetadata.name) return ""; // fetchuju, takže objekt existuje, ale je na něm jen isFetching

    return moduleMetadata.name;
  }
);

export const getModuleNameAndIdByTemplateId = createSelector(
  getModuleIdByTemplateId,
  getModuleNameByTemplateId,
  (moduleId, moduleName) => ({
    moduleId,
    moduleName,
  })
);

export const getAllModulesNameAndOrder = createSelector(
  getAllModulesMetadataById,
  (modulesMetadata) =>
    Object.values(modulesMetadata).map((metadata) => ({
      id: metadata?.id ?? 0,
      displayName: metadata?.name,
      order: metadata?.order ?? 0,
    }))
);

export const getDataForTemplatesOrder = createSelector(
  passId,
  getAllModulesMetadataById,
  (moduleId, modulesMetadata) => {
    const templatesMetadataForModule =
      modulesMetadata[moduleId].templates ?? [];

    return templatesMetadataForModule.map((metadata) => ({
      id: metadata?.id ?? 0,
      order: metadata?.order ?? 0,
      displayName: metadata?.name,
    }));
  }
);
